// InAppDocViewer/flag.js
//
// Helpers + safety guards for the in-app document viewer (v1 = PDF). The in-app
// PDF viewer is now unconditional: PDFs (per isPdfUrl below) open in the in-app
// viewer modal (page nav + zoom) and offer the per-document first-page-preview
// toggle. Non-PDF documents keep the legacy window.open(url, "_blank") behaviour
// — that fallback and the isPdfUrl scheme/host SSRF guard are deliberate and
// apply on every path.

// Hard size cap for inline first-page rendering. Above this we skip the canvas
// render and keep the thumbnail (large-PDF guard — see PR follow-ups). Bytes.
export const INAPP_DOC_PREVIEW_MAX_BYTES = 25 * 1024 * 1024; // 25 MB

// Reject loopback / link-local / private / reserved hosts to reduce SSRF and
// malicious-origin risk. Conservative: legitimate same-origin and public CDN
// PDFs (https) pass; only obviously-unroutable / internal targets are blocked.
const isBlockedHost = (host) => {
  if (!host) return true;
  const h = host.toLowerCase();
  if (h === "localhost" || h.endsWith(".localhost")) return true;
  if (h === "0.0.0.0" || h === "127.0.0.1") return true;
  // IPv6 loopback (URL host comes wrapped in brackets, e.g. "[::1]").
  if (h === "[::1]" || h === "::1") return true;
  // IPv4 dotted-quad ranges: loopback 127/8, link-local 169.254/16,
  // private 10/8, 172.16-31/12, 192.168/16.
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a === 127) return true;
    if (a === 169 && b === 254) return true;
    if (a === 10) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
  }
  return false;
};

// Only PDFs are handled in v1. Everything else keeps the legacy open-externally
// behaviour regardless of the flag (non-PDF convert-to-PDF is a scoped follow-up).
export const isPdfUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  // Parse so we can vet scheme + host (SSRF/malicious-origin guard). Anything
  // unparseable, non-https (rejects http:, file:, data:, blob:, etc.), or
  // pointing at a reserved/internal host is rejected.
  let parsed;
  try {
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : undefined;
    parsed = new URL(url, base);
  } catch (e) {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (isBlockedHost(parsed.hostname)) return false;
  // Strip query/hash, then test the extension. Conservative: only ".pdf".
  const clean = parsed.pathname.toLowerCase();
  return clean.endsWith(".pdf");
};
